import _ from "lodash";

interface OrderItem {
  order_ids: string[];
  amount_to_collect: number;
}

interface OrderMarkingMap {
  [key: string]: {
    key: string;
  };
}

const getNum = (num: number | string): number =>
  _.isFinite(Number(num)) ? Number(num) : 0;

export const calculateTotalPayableAmount = (
  listData: OrderItem[],
  orderMarkingMap: OrderMarkingMap,
  precision: number = 2,
): number => {
  const nums = listData.map((item) => {
    const isOrderCancelled =
      orderMarkingMap[item.order_ids[0]]?.key === "CANCEL_DELIVERY" ||
      orderMarkingMap[item.order_ids[0]]?.key === "CX_RTO";
    return isOrderCancelled ? 0 : item.amount_to_collect;
  });
  return Number(
    nums.reduce((acc, curr) => acc + getNum(curr), 0).toFixed(precision),
  );
};

// Verdict from the native capture screen. Optional throughout: the native
// module is absent on older binaries (CodePush ships JS ahead of the APK), and
// validation is off entirely unless the server turns it on — see
// @utils/proofValidationConfig.
export interface DeliveryProofImageValidation {
  verdict: "PASS" | "FORCED" | "SKIPPED";
  mode: string;
  attempts: number;
  ml_skipped: boolean;
  objects: number;
  scores: Record<string, number>;
  failed: string[];
  skipped: string[];
  /** goods_present only — what the classifier saw, e.g. "Car"; absent otherwise. */
  subject?: string | null;
  /** "person" | "vehicle" | "building" | "other" from the server's deny_labels keys. */
  subject_category?: string | null;
  /** Top classifier labels across detected objects (max 10), whenever detection ran. */
  labels?: { label: string; confidence: number }[];
}

export interface DeliveryProofImage {
  url: string;
  lat: number | null;
  lng: number | null;
  uploaded_at: string;
  validation?: DeliveryProofImageValidation | null;
}

// Fields optional/nullable to stay assignable from every build site's shape
// (CustomerOrderView's onPopupSubmit always populates all four; the OTP
// screen's pre-existing RouteParams.plastic_bag type — unrelated to this
// feature — carries the same fields as optional/nullable).
export interface PlasticBag {
  opted_in?: boolean;
  delivered?: boolean;
  count?: number | null;
  delivered_count?: number | null;
}

export interface DeliveryProof {
  popup_variant: "POLYBAG" | "ITEM_VERIFICATION";
  images: DeliveryProofImage[];
}

// Shared payload-build logic for the delivery-proof popup, used at every
// `cp-cx-complete-delivery` spread-site (CustomerOrderView, CpCxQrPayment,
// NewOTPVerification) so the plastic_bag/delivery_proof shape can't drift
// between sites. `plasticBag: null` (P2, or popup_config off) omits
// `plastic_bag` entirely — the caller decides that, not this helper.
export const buildDeliveryProofPayload = ({
  plasticBag,
  deliveryProof,
}: {
  plasticBag?: PlasticBag | null;
  deliveryProof?: DeliveryProof | null;
}): { plastic_bag?: PlasticBag; delivery_proof?: DeliveryProof } => ({
  ...(plasticBag ? { plastic_bag: plasticBag } : {}),
  ...(deliveryProof ? { delivery_proof: deliveryProof } : {}),
});
